import React, { useEffect, useState, useCallback } from 'react';
import { updateGroupsStorage as updateGroupsStorageAction } from 'store/groups/actions';
import { DocumentGroup } from 'types/documentGroup';
import produce from 'immer';
import { useActions, useShallowSelector } from 'hooks';
import { updateDocuments as updateDocumentsAction } from 'store/jobs/actions';
import { ApplicationState } from 'store/types';
import styled from 'styled-components';
import { JobOutput } from 'types';
import DocumentGroupDrawer from '../drawers/groups';
import GroupList from './GroupList';
import Styled from '../drawers/drawers.styled';

const { StyledScrollbar } = Styled;

const StyledWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const StyledStrechedScrollbar = styled(StyledScrollbar)`
  display: flex;
  width: 100%;
  height: 100%;
`;

const DocumentGroupContainer = () => {
  const { stateGroups } = useShallowSelector(({ groups }: ApplicationState) => ({
    stateGroups: groups,
  }));

  const { stateDocuments } = useShallowSelector(({ jobs }: ApplicationState) => ({
    stateDocuments: jobs.documents,
  }));

  const [allDocuments, setDocuments] = useState<JobOutput[]>(stateDocuments);

  const { updateDocuments } = useActions({
    updateDocuments: updateDocumentsAction,
  });

  useEffect(() => {
    const uploadedJobFiles = allDocuments.filter((jobFile) => jobFile.documentsGroupId);
    updateDocuments(uploadedJobFiles);
  }, [updateDocuments, allDocuments]);

  const { updateGroupsStorage } = useActions({
    updateGroupsStorage: updateGroupsStorageAction,
  });

  const [documentGroupsState, setDocumentGroups] = useState<DocumentGroup[]>(
    stateGroups.documentGroups,
  );

  const addGroup = (documentsGroup: DocumentGroup) => {
    const updatedDocumentsGroup: DocumentGroup[] = [...documentGroupsState, documentsGroup];
    setDocumentGroups(updatedDocumentsGroup);
  };

  const handleUpdate = useCallback(async (documentsGroup: DocumentGroup) => {
    setDocumentGroups((documentGroups) =>
      produce(documentGroups, (groupsDraft) => {
        const groupDraft = groupsDraft.find((j) => j.id === documentsGroup.id)!;
        groupDraft.processingJob = documentsGroup.processingJob;
      }),
    );
  }, []);

  const handleRemove = useCallback(
    (groupId: string) => {
      setDocumentGroups((documentsGroup) =>
        documentsGroup.filter((group) => group && group.id !== groupId),
      );
      const existingDocumentsGroupIds = documentGroupsState
        .filter((group) => group && group.id !== groupId)
        .map((group) => group.id);

      setDocuments((documents) =>
        documents.filter(
          (doc) => existingDocumentsGroupIds.findIndex((id) => id === doc.documentsGroupId) !== -1,
        ),
      );
    },
    [documentGroupsState],
  );

  useEffect(() => {
    updateGroupsStorage(documentGroupsState);
  }, [updateGroupsStorage, documentGroupsState]);

  return (
    <StyledWrapper>
      <DocumentGroupDrawer addGroup={addGroup} />
      <StyledStrechedScrollbar>
        <GroupList
          documentGroups={documentGroupsState}
          documents={allDocuments}
          setDocuments={setDocuments}
          onRemove={handleRemove}
          onUpdate={handleUpdate}
        />
      </StyledStrechedScrollbar>
    </StyledWrapper>
  );
};

export default DocumentGroupContainer;
